import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

const API = "https://ceo-cockpit-3npo.onrender.com";

interface Sheet { name: string; supported: boolean; }
interface LogEntry {
  id: number; sheet_name: string; imported_by: string;
  imported_at: string; file_name: string; new_records: number;
  updated_records: number; unchanged_records: number;
  status: string; version_tag: string | null; selected_sheets: string | null; notes: string;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function DataHub() {
  const { user } = useAuth();
  const canUpload = user?.role === "CFO" || user?.role === "admin";
  const [sheets,      setSheets]      = useState<Sheet[]>([]);
  const [logs,        setLogs]        = useState<LogEntry[]>([]);
  const [selected,    setSelected]    = useState<string[]>(["Monthly P&L", "Channel P&L"]);
  const [versionTag,  setVersionTag]  = useState("");
  const [file,        setFile]        = useState<File | null>(null);
  const [stage,       setStage]       = useState<any>(null);
  const [importing,   setImporting]   = useState(false);
  const [statusMsg,   setStatusMsg]   = useState<{ type: "success"|"error"|"info"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch(`${API}/sheets`).then(r => r.json()).then(d => setSheets(d.sheets));
    fetchLogs();
  }, []);

  function fetchLogs() {
    fetch(`${API}/import-log`).then(r => r.json()).then(d => setLogs(d.logs));
  }

  function toggleSheet(name: string) {
    setSelected(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);
  }

  async function handleStage() {
    if (!file || selected.length === 0) return;
    setImporting(true);
    setStatusMsg({ type: "info", text: "Previewing changes..." });
    const form = new FormData();
    form.append("file", file);
    form.append("selected_sheets", selected.join(","));
    form.append("action", "stage");
    form.append("imported_by", "sandeep@ampm.in");
    form.append("version_tag", versionTag);
    try {
      const res  = await fetch(`${API}/import`, { method: "POST", body: form });
      const json = await res.json();
      setStage(json);
      setStatusMsg({ type: "info", text: "Preview ready. Review changes below and click Commit to save." });
    } catch {
      setStatusMsg({ type: "error", text: "Preview failed. Check the API is running." });
    } finally {
      setImporting(false);
    }
  }

  async function handleCommit() {
    if (!file || selected.length === 0) return;
    setImporting(true);
    setStatusMsg({ type: "info", text: "Importing data... please do not close this window." });
    const form = new FormData();
    form.append("file", file);
    form.append("selected_sheets", selected.join(","));
    form.append("action", "commit");
    form.append("imported_by", "sandeep@ampm.in");
    form.append("version_tag", versionTag);
    try {
      const res  = await fetch(`${API}/import`, { method: "POST", body: form });
      await res.json();
      setStatusMsg({ type: "success", text: "Import committed successfully." });
      setStage(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchLogs();
    } catch {
      setStatusMsg({ type: "error", text: "Commit failed. Check the API is running." });
    } finally {
      setImporting(false);
    }
  }

  const canPreview = file !== null && selected.length > 0 && !importing;
  const canCommit  = stage !== null && !importing;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: "20px" }}>
        <h1 style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>Data Hub</h1>
        <p style={{ fontSize: "12px", color: "var(--text-secondary)", margin: "2px 0 0" }}>Upload Excel -- Review diff -- Commit with version tag</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>

        {/* LEFT --" Upload Panel */}
        <div style={{ border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", backgroundColor: "var(--bg-header)", borderBottom: "1px solid var(--border-sidebar)" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.5px" }}>Monthly Upload</span>
          </div>
          {canUpload && <div style={{ padding: "20px" }}>

            {/* File picker */}
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: "2px dashed var(--border)", borderRadius: "8px", padding: "24px",
                textAlign: "center", cursor: "pointer", marginBottom: "16px",
                backgroundColor: file ? "var(--bg-secondary)" : "transparent",
                transition: "background-color 0.15s",
              }}
            >
              <div style={{ fontSize: "22px", marginBottom: "6px" }}>--"-</div>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)" }}>
                {file ? file.name : "Click to select Excel file"}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
                {file ? `${(file.size / 1024).toFixed(1)} KB` : "Accepts .xlsx files only"}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={e => {
                  setFile(e.target.files?.[0] ?? null);
                  setStage(null);
                  setStatusMsg(null);
                }}
              />
            </div>

            {/* Sheet selection */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Select Sheets to Import
              </div>
              {sheets.map(s => (
                <label key={s.name} style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "7px 10px", borderRadius: "6px", marginBottom: "4px",
                  cursor: s.supported ? "pointer" : "default",
                  backgroundColor: selected.includes(s.name) ? "var(--bg-secondary)" : "transparent",
                  border: "1px solid",
                  borderColor: selected.includes(s.name) ? "var(--accent-gold)" : "var(--border)",
                  opacity: s.supported ? 1 : 0.4,
                }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(s.name)}
                    disabled={!s.supported}
                    onChange={() => s.supported && toggleSheet(s.name)}
                    style={{ accentColor: "var(--accent-gold)", width: "14px", height: "14px" }}
                  />
                  <span style={{ fontSize: "12px", color: "var(--text-primary)", flex: 1 }}>{s.name}</span>
                  {!s.supported && (
                    <span style={{ fontSize: "9px", color: "var(--text-muted)", backgroundColor: "var(--bg-secondary)", padding: "2px 6px", borderRadius: "3px" }}>soon</span>
                  )}
                </label>
              ))}
            </div>

            {/* Version tag */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Version Tag (optional)
              </div>
              <input
                type="text"
                value={versionTag}
                onChange={e => setVersionTag(e.target.value)}
                placeholder="e.g. Sep-26_v1"
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: "6px",
                  border: "1px solid var(--border)", backgroundColor: "var(--bg-primary)",
                  color: "var(--text-primary)", fontSize: "12px", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Status message */}
            {statusMsg && (
              <div style={{
                padding: "10px 12px", borderRadius: "6px", marginBottom: "12px", fontSize: "12px",
                backgroundColor: statusMsg.type === "success" ? "#F0FDF4" : statusMsg.type === "error" ? "#FEF2F2" : "var(--bg-secondary)",
                border: `1px solid ${statusMsg.type === "success" ? "#BBF7D0" : statusMsg.type === "error" ? "#FECACA" : "var(--border)"}`,
                color: statusMsg.type === "success" ? "#16A34A" : statusMsg.type === "error" ? "#DC2626" : "var(--text-secondary)",
              }}>
                {statusMsg.text}
              </div>
            )}

            {/* Stage preview results */}
            {stage && (
              <div style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", padding: "12px", marginBottom: "12px" }}>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px" }}>IMPORT PREVIEW</div>
                {stage.sheets?.map((s: any) => (
                  <div key={s.sheet} style={{ marginBottom: "6px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>{s.sheet}</div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>
                      <span style={{ color: "var(--delta-positive)" }}>+{s.new_records} new</span>
                      {" -- "}
                      <span style={{ color: "var(--accent-gold)" }}>{s.updated_records} updated</span>
                      {" -- "}
                      <span style={{ color: "var(--text-muted)" }}>{s.unchanged_records} unchanged</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleStage}
                disabled={!canPreview}
                style={{
                  flex: 1, padding: "9px 16px", borderRadius: "6px", border: "1px solid var(--accent-gold)",
                  backgroundColor: "transparent", color: canPreview ? "var(--accent-gold)" : "var(--text-muted)",
                  fontSize: "12px", fontWeight: 600, cursor: canPreview ? "pointer" : "default",
                  transition: "all 0.15s",
                }}
              >
                {importing && !stage ? "Previewing..." : "Preview Import"}
              </button>
              {stage && (
                <button
                  onClick={handleCommit}
                  disabled={!canCommit}
                  style={{
                    flex: 1, padding: "9px 16px", borderRadius: "6px", border: "none",
                    backgroundColor: canCommit ? "var(--accent-gold)" : "var(--border)",
                    color: canCommit ? "#0F1C2E" : "var(--text-muted)",
                    fontSize: "12px", fontWeight: 700, cursor: canCommit ? "pointer" : "default",
                    transition: "all 0.15s",
                  }}
                >
                  {importing ? "Committing..." : "Commit Import"}
                </button>
              )}
            </div>
          </div>}
          {!canUpload && (
            <div style={{ padding:"16px", backgroundColor:"var(--bg-secondary)", border:"1px solid var(--border)", borderRadius:"8px", color:"var(--text-muted)", fontSize:"12px", margin:"16px" }}>
              You have view-only access. Contact your CFO to upload data.
            </div>
          )}
          {/* Upload History */}
          <div style={{ borderTop: "1px solid var(--border)" }}>
            <div style={{ padding: "12px 16px", backgroundColor: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" }}>
              <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Upload History</span>
            </div>
            {logs.length === 0 ? (
              <div style={{ padding: "16px", fontSize: "12px", color: "var(--text-muted)", textAlign: "center" }}>No imports yet</div>
            ) : (
              logs.slice(0, 8).map(log => (
                <div key={log.id} style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12px", fontWeight: 500, color: "var(--text-primary)" }}>
                      {log.version_tag || log.file_name}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                      {log.sheet_name} -- {log.imported_by} -- {fmtDate(log.imported_at)}
                    </div>
                  </div>
                  <span style={{
                    fontSize: "10px", fontWeight: 600, padding: "2px 8px", borderRadius: "4px",
                    backgroundColor: log.status === "COMMITTED" ? "#F0FDF4" : "#FFFBEB",
                    color: log.status === "COMMITTED" ? "#16A34A" : "#D97706",
                    border: `1px solid ${log.status === "COMMITTED" ? "#BBF7D0" : "#FDE68A"}`,
                  }}>
                    {log.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT --" Audit Trail */}
        <div style={{ border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", backgroundColor: "var(--bg-header)", borderBottom: "1px solid var(--border-sidebar)" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.5px" }}>Audit Trail</span>
          </div>
          <div style={{ overflowY: "auto", maxHeight: "600px" }}>
            {logs.length === 0 ? (
              <div style={{ padding: "32px", fontSize: "12px", color: "var(--text-muted)", textAlign: "center" }}>No activity yet</div>
            ) : (
              logs.map(log => (
                <div key={log.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", gap: "12px" }}>
                  <div style={{ fontSize: "10px", color: "var(--text-muted)", whiteSpace: "nowrap", paddingTop: "2px", minWidth: "120px" }}>
                    {fmtDate(log.imported_at)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--accent-gold)", marginBottom: "2px" }}>
                      {log.imported_by}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      {log.status} {log.sheet_name} --" {log.new_records} new -- {log.updated_records} updated -- {log.unchanged_records} unchanged
                    </div>
                    {log.version_tag && (
                      <div style={{ fontSize: "10px", color: "var(--text-muted)", marginTop: "2px" }}>
                        Tag: {log.version_tag}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



